import React, { useState, useEffect, useRef } from "react";
import { Container, Form, Button, Card, Alert, Row, Col, Spinner } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { fetchMyGroups, updateMyGroup } from '../services/UserGroupService';
import { uploadImagePhysicalFile } from '../services/FileUploadService';
import { fetchStates } from '../services/LocationService';
import GroupData from '../models/GroupData';
import StateData from '../models/StateData';
import CountryData from '../models/CountryData';
import { IMAGE_BASE } from '../Config';
import { validateEmail, validatePhone, validateURL, validatePinCode, validateDescription, validateAddress } from '../utils/validation';

interface EditingGroup {
  id: string;
  field: string;
  value: string;
}

interface ToastNotification {
  id: string;
  message: string;
  type: 'success' | 'error';
}

function ManageGroupPage() {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [editing, setEditing] = useState<EditingGroup | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalGroups, setTotalGroups] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [countries] = useState<CountryData[]>([{ _id: 'IN', name: 'India', countryCode: 'IN' }]);
  const [states, setStates] = useState<StateData[]>([]);
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    const toast: ToastNotification = {
      id: Date.now().toString(),
      message,
      type
    };
    
    setToasts(prev => [...prev, toast]);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== toast.id));
    }, 3000);
  };

  useEffect(() => {
    loadMyGroups();
    loadStates();
  }, []);

  const loadStates = async () => {
    try {
      const statesData = await fetchStates();
      setStates(statesData);
    } catch (error) {
      console.error('Failed to load states:', error);
    }
  };

  const loadMyGroups = async (page: number = 1, search: string = '', append: boolean = false) => {
    try {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      
      const data = await fetchMyGroups(page, 12, search);
      
      if (append) {
        setGroups(prev => [...prev, ...(data.groups || [])]);
      } else {
        setGroups(data.groups || []);
      }
      
      setCurrentPage(data.currentPage);
      setHasMore(data.hasMore);
      setTotalGroups(data.totalGroups);
    } catch (error: any) {
      showNotification(error.response?.data?.error || 'Failed to load groups', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    loadMyGroups(1, term, false);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    loadMyGroups(nextPage, searchTerm, true);
  };

  const validateGroupData = (group: GroupData): boolean => {
    const errors: {[key: string]: string} = {};
    
    // Validate email if provided
    if (group.email) {
      const emailValidation = validateEmail(group.email);
      if (!emailValidation.isValid) {
        errors[`${group._id}_email`] = emailValidation.message || '';
      }
    }
    
    // Validate phone if provided
    if (group.phone) {
      const phoneValidation = validatePhone(group.phone);
      if (!phoneValidation.isValid) {
        errors[`${group._id}_phone`] = phoneValidation.message || '';
      }
    }
    
    // Validate website URL if provided
    const urlValidation = validateURL(group.website);
    if (!urlValidation.isValid) {
      errors[`${group._id}_website`] = urlValidation.message || '';
    }
    
    // Validate pin code if provided
    const pinValidation = validatePinCode(group.pinCode);
    if (!pinValidation.isValid) {
      errors[`${group._id}_pinCode`] = pinValidation.message || '';
    }
    
    // Validate description if provided
    const descValidation = validateDescription(group.description);
    if (!descValidation.isValid) {
      errors[`${group._id}_description`] = descValidation.message || '';
    }
    
    // Validate address if provided
    const addressValidation = validateAddress(group.address);
    if (!addressValidation.isValid) {
      errors[`${group._id}_address`] = addressValidation.message || '';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveGroup = async (group: GroupData) => {
    if (!validateGroupData(group)) {
      showNotification('Please fix validation errors before saving', 'error');
      return;
    }
    
    try {
      setSaving(true);
      const updateData = {
        description: group.description,
        address: group.address,
        phone: group.phone,
        email: group.email,
        website: group.website,
        country: group.country,
        state: group.state,
        pinCode: group.pinCode
      };
      
      await updateMyGroup(group._id!, updateData);
      setEditing(null);
      setValidationErrors({});
      showNotification('Group updated successfully');
    } catch (error: any) {
      showNotification(error.response?.data?.error || 'Failed to update group', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(null);
  };

  const handleImageUpload = async (groupId: string, file: File) => {
    try {
      setUploadingImage(groupId);
      const uploadResult = await uploadImagePhysicalFile(file, 'group_logo');
      const imageUrl = uploadResult.filePath || uploadResult.url;
      
      await updateMyGroup(groupId, { groupImage: imageUrl });
      
      // Update local state
      setGroups(groups.map(group => 
        group._id === groupId 
          ? { ...group, groupImage: imageUrl }
          : group
      ));
      
      showNotification('Group image updated successfully');
    } catch (error: any) {
      showNotification(error.response?.data?.error || 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(null);
    }
  };

  const triggerImageUpload = (groupId: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          handleImageUpload(groupId, file);
        }
      };
      fileInputRef.current.click();
    }
  };



  if (loading) {
    return (
      <PermissionWrapper pageName="ManageGroupPage">
        <Container className="mt-5 text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading your groups...</p>
        </Container>
      </PermissionWrapper>
    );
  }

  return (
    <PermissionWrapper pageName="ManageGroupPage">
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>My Groups</h2>
          {totalGroups > 0 && (
            <small className="text-muted">{totalGroups} group{totalGroups !== 1 ? 's' : ''} found</small>
          )}
        </div>

        {totalGroups > 10 && (
          <div className="mb-4">
            <Form.Control
              type="text"
              placeholder="Search groups by name, description, or address..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        )}

        {groups.length === 0 ? (
          <Card>
            <Card.Body className="text-center py-5">
              <i className="bi bi-people" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
              <h4 className="mt-3 text-muted">No Groups Found</h4>
              <p className="text-muted">You are not associated with any theater groups yet.</p>
            </Card.Body>
          </Card>
        ) : (
          <Row>
            {Array.isArray(groups) && groups.map((group) => {
              const isEditingGroup = editing?.id === group._id;
              return (
                <Col key={group._id} lg={12} className="mb-4">
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">{group.name}</h5>
                      {!isEditingGroup ? (
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => setEditing({ id: group._id!, field: 'edit', value: '' })}
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </Button>
                      ) : (
                        <div>
                          <Button 
                            variant="success" 
                            size="sm" 
                            className="me-2"
                            onClick={() => setEditing(null)}
                          >
                            <i className="bi bi-check"></i> Done
                          </Button>
                        </div>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        {/* Group Image */}
                        <Col md={3} className="text-center mb-3">
                          <div 
                            className="border rounded" 
                            style={{ 
                              width: '150px', 
                              height: '150px', 
                              margin: '0 auto',
                              overflow: 'hidden',
                              backgroundColor: '#f8f9fa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {group.groupImage ? (
                              <img 
                                src={IMAGE_BASE + group.groupImage} 
                                alt={group.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                                }}
                              />
                            ) : (
                              <i className="bi bi-image" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                            )}
                          </div>
                          {isEditingGroup && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => triggerImageUpload(group._id!)}
                              disabled={uploadingImage === group._id}
                              className="mt-2"
                            >
                              {uploadingImage === group._id ? (
                                <><Spinner animation="border" size="sm" /> Uploading...</>
                              ) : (
                                <><i className="bi bi-camera"></i> Change Image</>
                              )}
                            </Button>
                          )}
                        </Col>

                        {/* Group Details */}
                        <Col md={9}>
                          <Row>
                            <Col md={6}>
                              <div className="mb-3">
                                <strong>Description:</strong>
                                {isEditingGroup ? (
                                  <>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={group.description || ''}
                                      onChange={(e) => {
                                        const updatedGroups = groups.map(g => 
                                          g._id === group._id ? { ...g, description: e.target.value } : g
                                        );
                                        setGroups(updatedGroups);
                                        // Clear validation error on change
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors[`${group._id}_description`];
                                          return newErrors;
                                        });
                                      }}
                                      className="mt-1"
                                      isInvalid={!!validationErrors[`${group._id}_description`]}
                                    />
                                    {validationErrors[`${group._id}_description`] && (
                                      <Form.Control.Feedback type="invalid">
                                        {validationErrors[`${group._id}_description`]}
                                      </Form.Control.Feedback>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-muted mt-1">{group.description || 'No description'}</div>
                                )}
                              </div>
                              
                              <div className="mb-3">
                                <strong>Address:</strong>
                                {isEditingGroup ? (
                                  <>
                                    <Form.Control
                                      type="text"
                                      value={group.address || ''}
                                      onChange={(e) => {
                                        const updatedGroups = groups.map(g => 
                                          g._id === group._id ? { ...g, address: e.target.value } : g
                                        );
                                        setGroups(updatedGroups);
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors[`${group._id}_address`];
                                          return newErrors;
                                        });
                                      }}
                                      className="mt-1"
                                      isInvalid={!!validationErrors[`${group._id}_address`]}
                                    />
                                    {validationErrors[`${group._id}_address`] && (
                                      <Form.Control.Feedback type="invalid">
                                        {validationErrors[`${group._id}_address`]}
                                      </Form.Control.Feedback>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-muted mt-1">{group.address || 'No address'}</div>
                                )}
                              </div>
                              
                              <div className="mb-3">
                                <strong>Phone:</strong>
                                {isEditingGroup ? (
                                  <>
                                    <Form.Control
                                      type="tel"
                                      value={group.phone || ''}
                                      onChange={(e) => {
                                        const updatedGroups = groups.map(g => 
                                          g._id === group._id ? { ...g, phone: e.target.value } : g
                                        );
                                        setGroups(updatedGroups);
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors[`${group._id}_phone`];
                                          return newErrors;
                                        });
                                      }}
                                      className="mt-1"
                                      isInvalid={!!validationErrors[`${group._id}_phone`]}
                                    />
                                    {validationErrors[`${group._id}_phone`] && (
                                      <Form.Control.Feedback type="invalid">
                                        {validationErrors[`${group._id}_phone`]}
                                      </Form.Control.Feedback>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-muted mt-1">{group.phone || 'No phone'}</div>
                                )}
                              </div>
                            </Col>
                            
                            <Col md={6}>
                              <div className="mb-3">
                                <strong>Email:</strong>
                                {isEditingGroup ? (
                                  <>
                                    <Form.Control
                                      type="email"
                                      value={group.email || ''}
                                      onChange={(e) => {
                                        const updatedGroups = groups.map(g => 
                                          g._id === group._id ? { ...g, email: e.target.value } : g
                                        );
                                        setGroups(updatedGroups);
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors[`${group._id}_email`];
                                          return newErrors;
                                        });
                                      }}
                                      className="mt-1"
                                      isInvalid={!!validationErrors[`${group._id}_email`]}
                                    />
                                    {validationErrors[`${group._id}_email`] && (
                                      <Form.Control.Feedback type="invalid">
                                        {validationErrors[`${group._id}_email`]}
                                      </Form.Control.Feedback>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-muted mt-1">{group.email || 'No email'}</div>
                                )}
                              </div>
                              
                              <div className="mb-3">
                                <strong>Website:</strong>
                                {isEditingGroup ? (
                                  <>
                                    <Form.Control
                                      type="url"
                                      value={group.website || ''}
                                      onChange={(e) => {
                                        const updatedGroups = groups.map(g => 
                                          g._id === group._id ? { ...g, website: e.target.value } : g
                                        );
                                        setGroups(updatedGroups);
                                        setValidationErrors(prev => {
                                          const newErrors = { ...prev };
                                          delete newErrors[`${group._id}_website`];
                                          return newErrors;
                                        });
                                      }}
                                      className="mt-1"
                                      isInvalid={!!validationErrors[`${group._id}_website`]}
                                    />
                                    {validationErrors[`${group._id}_website`] && (
                                      <Form.Control.Feedback type="invalid">
                                        {validationErrors[`${group._id}_website`]}
                                      </Form.Control.Feedback>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-muted mt-1">
                                    {group.website ? (
                                      <a href={group.website} target="_blank" rel="noopener noreferrer">
                                        {group.website}
                                      </a>
                                    ) : (
                                      'No website'
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <Row>
                                <Col sm={4}>
                                  <div className="mb-3">
                                    <strong>Country:</strong>
                                    {isEditingGroup ? (
                                      <Form.Select
                                        value={group.country || ''}
                                        onChange={(e) => {
                                          const updatedGroups = groups.map(g => 
                                            g._id === group._id ? { ...g, country: e.target.value } : g
                                          );
                                          setGroups(updatedGroups);
                                        }}
                                        className="mt-1"
                                      >
                                        <option value="">Select Country</option>
                                        {countries.map(country => (
                                          <option key={country.countryCode} value={country.countryCode}>
                                            {country.name}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    ) : (
                                      <div className="text-muted mt-1">
                                        {countries.find(c => c.countryCode === group.country)?.name || group.country || 'N/A'}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                <Col sm={4}>
                                  <div className="mb-3">
                                    <strong>State:</strong>
                                    {isEditingGroup ? (
                                      <Form.Select
                                        value={group.state || ''}
                                        onChange={(e) => {
                                          const updatedGroups = groups.map(g => 
                                            g._id === group._id ? { ...g, state: e.target.value } : g
                                          );
                                          setGroups(updatedGroups);
                                        }}
                                        className="mt-1"
                                      >
                                        <option value="">Select State</option>
                                        {states
                                          .filter(state => state.countryCode === group.country)
                                          .map(state => (
                                            <option key={state.stateCode} value={state.stateCode}>
                                              {state.name}
                                            </option>
                                          ))}
                                      </Form.Select>
                                    ) : (
                                      <div className="text-muted mt-1">
                                        {states.find(s => s.stateCode === group.state)?.name || group.state || 'N/A'}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                                <Col sm={4}>
                                  <div className="mb-3">
                                    <strong>Pin Code:</strong>
                                    {isEditingGroup ? (
                                      <>
                                        <Form.Control
                                          type="text"
                                          value={group.pinCode || ''}
                                          onChange={(e) => {
                                            const updatedGroups = groups.map(g => 
                                              g._id === group._id ? { ...g, pinCode: e.target.value } : g
                                            );
                                            setGroups(updatedGroups);
                                            setValidationErrors(prev => {
                                              const newErrors = { ...prev };
                                              delete newErrors[`${group._id}_pinCode`];
                                              return newErrors;
                                            });
                                          }}
                                          className="mt-1"
                                          isInvalid={!!validationErrors[`${group._id}_pinCode`]}
                                        />
                                        {validationErrors[`${group._id}_pinCode`] && (
                                          <Form.Control.Feedback type="invalid">
                                            {validationErrors[`${group._id}_pinCode`]}
                                          </Form.Control.Feedback>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-muted mt-1">{group.pinCode || 'N/A'}</div>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                          
                          {isEditingGroup && (
                            <div className="mt-3 text-end">
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => {
                                  setEditing(null);
                                  loadMyGroups(); // Reload to reset changes
                                }}
                              >
                                Cancel
                              </Button>
                              <Button 
                                variant="primary" 
                                size="sm"
                                onClick={() => handleSaveGroup(group)}
                                disabled={saving}
                              >
                                {saving ? (
                                  <><Spinner animation="border" size="sm" /> Saving...</>
                                ) : (
                                  'Save Changes'
                                )}
                              </Button>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
        
        {hasMore && (
          <div className="text-center mt-4">
            <Button 
              variant="outline-primary" 
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <><Spinner animation="border" size="sm" /> Loading...</>
              ) : (
                'Load More Groups'
              )}
            </Button>
          </div>
        )}
        
        {/* Hidden file input for image uploads */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
        />
        
        {/* Toast Notifications */}
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`alert alert-${toast.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}
              role="alert"
            >
              <i className={`bi bi-${toast.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
              {toast.message}
              <button
                type="button"
                className="btn-close"
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              ></button>
            </div>
          ))}
        </div>
      </Container>
    </PermissionWrapper>
  );
}

export default ManageGroupPage;
